import styled, { css } from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  white: "#ffffff",
  title: "#293142",
  muted: "#7d8599",
  line: "#e1e6ef",
  brand: "#ef4f5f",
};

const MOBILE = "@media (max-width: 480px)";

export const ShippingNoteTabs = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  margin: 0 -24px 24px;
  padding: 0 24px 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${MOBILE} {
    gap: 10px;
    margin: 0 -16px 20px;
    padding: 0 16px 4px;
  }
`;

export const ShippingNoteTab = styled.button`
  min-height: 36px;
  flex: 0 0 auto;
  border: 1.5px solid ${({ $active }) => ($active ? COLORS.brand : COLORS.line)};
  border-radius: 9px;
  padding: 0 14px;
  background: ${({ $active }) => ($active ? "#fffafa" : COLORS.white)};
  box-shadow: ${({ $active }) =>
    $active ? "none" : "0 2px 6px rgba(41, 49, 66, 0.10)"};
  color: ${({ $active }) => ($active ? COLORS.title : COLORS.muted)};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;

  &:active {
    transform: scale(0.98);
  }

  ${MOBILE} {
    min-height: 34px;
    padding: 0 12px;
    font-size: 13px;
  }
`;

export const ShippingNoteCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${MOBILE} {
    gap: 18px;
  }
`;

export const ShippingNoteCard = styled.article`
  overflow: hidden;
  border-radius: 22px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);

  ${MOBILE} {
    border-radius: 18px;
  }
`;

const statusPalette = ({ $status }) => {
  const status = String($status || "").toUpperCase();

  if (status === "FULFILLED" || status === "PARTIALLY_FULFILLED") {
    return css`
      background: #e4f5f7;
      color: #087b82;
    `;
  }

  if (status === "CANCELLED" || status === "DELETED" || status === "NO_SHOW") {
    return css`
      background: #f1f3f7;
      color: #7d8599;
    `;
  }

  return css`
    background: #e4eeff;
    color: #1479ff;
  `;
};

export const ShippingNoteStatus = styled.span`
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  border-radius: 0 0 4px 4px;
  margin-left: 18px;
  padding: 0 10px;
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  ${statusPalette}

  ${MOBILE} {
    min-height: 27px;
    margin-left: 14px;
    padding: 0 8px;
    font-size: 12px;
  }
`;

export const ShippingNoteMain = styled.div`
  padding: 8px 18px 18px;

  ${MOBILE} {
    padding: 7px 14px 14px;
  }
`;

export const ShippingNoteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const ShippingNoteTitle = styled.h2`
  overflow: hidden;
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const ShippingNoteSubtitle = styled.p`
  margin: 4px 0 0;
  color: ${COLORS.muted};
  font-size: 13px;
  line-height: 1.3;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const ShippingNoteArrow = styled.button`
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  cursor: pointer;

  &:active {
    transform: scale(0.96);
  }

  ${MOBILE} {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
  }
`;

export const ShippingNoteMetadata = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 18px;
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  padding: 12px 6px;

  ${MOBILE} {
    margin-top: 15px;
    border-radius: 11px;
    padding: 10px 2px;
  }
`;

export const ShippingNoteMetaItem = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
  font-size: 13px;
  line-height: 1.25;
  text-align: center;

  svg {
    width: 18px;
    height: 18px;
    color: ${COLORS.muted};
    stroke-width: 1.9;
  }

  span {
    width: 100%;
    overflow: hidden;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${MOBILE} {
    gap: 6px;
    font-size: 12px;

    span {
      font-size: 12px;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const DeliveryDetails = styled.footer`
  display: flex;
  min-height: 64px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid #f1f3f7;
  padding: 12px 18px;
  background: linear-gradient(90deg, #ffffff 0%, #fafbfe 100%);

  ${MOBILE} {
    min-height: 56px;
    padding: 10px 14px;
  }
`;

export const DeliveryCopy = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${COLORS.title};
    font-family: ${fontFamilies.semiBold};
    font-size: 14px;
  }

  span {
    margin-top: 3px;
    color: ${COLORS.muted};
    font-size: 12px;
  }

  ${MOBILE} {
    strong {
      font-size: 13px;
    }

    span {
      font-size: 11px;
    }
  }
`;

export const CallButton = styled.a`
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  color: ${COLORS.brand};
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.8;
  }

  ${MOBILE} {
    width: 36px;
    height: 36px;
    flex-basis: 36px;

    svg {
      width: 16px;
      height: 22px;
    }
  }
`;
